import type { Control, FieldValues, Path } from 'react-hook-form';
import { TextInput } from 'react-native-paper';

import { DownIcon } from '@/assets';
import { RotatableView } from '@/shared/components/animated/RotatableView';
import { isIOS } from '@/shared/utils/platform';

import { GeoAreaSelectField } from './GeoAreaSelectField';

type Props<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  label: string;
  placeholder: string;
  organizationId: string;
  sheetTitle?: string;
  helperText?: string;
  editable?: boolean;
};

/**
 * Outlined form-field variant of the geo-area picker. Used in traditional
 * forms (e.g. organization edit). All logic lives in {@link GeoAreaSelectField};
 * this component only supplies the outlined `TextInput` trigger.
 */
export const FormGeoAreaSelect = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  organizationId,
  sheetTitle,
  helperText,
  editable = true
}: Props<T>) => (
  <GeoAreaSelectField<T>
    name={name}
    control={control}
    label={label}
    organizationId={organizationId}
    sheetTitle={sheetTitle}
    helperText={helperText}
    editable={editable}
    renderTrigger={({ resolvedNames, isOpen, hasError, open, inputRef }) => (
      <TextInput
        ref={inputRef}
        label={label}
        placeholder={placeholder}
        accessibilityLabel={label}
        accessibilityHint={placeholder}
        mode="outlined"
        error={hasError}
        caretHidden
        showSoftInputOnFocus={false}
        editable={editable}
        style={{ backgroundColor: 'transparent' }}
        value={resolvedNames}
        right={
          <TextInput.Icon
            icon={({ color, size }) => (
              <RotatableView changeRotation={isOpen}>
                <DownIcon width={size} height={size} fill={color} />
              </RotatableView>
            )}
            onPress={open}
          />
        }
        onPress={isIOS ? open : undefined}
        onFocus={isIOS ? undefined : open}
      />
    )}
  />
);
